import { FC } from "react";
import { useIntl } from "react-intl";
import type { ChronologyEventReference } from "~/api/model";
import { SourceBadge } from "~/components/SourceBadge";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { trackEvent } from "~/utils/analytics";

interface ChronologySourceBadgeProps {
  reference: ChronologyEventReference;
}

/**
 * Renders the clickable page citation for a chronology event. Clicking
 * navigates the document viewer to the cited page, mirroring the citation
 * affordance used in reports and chat. The badge is only interactive when the
 * reference resolves to an actual page (no pageId without a page).
 */
export const ChronologySourceBadge: FC<ChronologySourceBadgeProps> = ({
  reference,
}) => {
  const intl = useIntl();
  const [, updateParams] = useDocumentSearchParams();

  const { pageId, pageNumber, batesNumber } = reference;

  const displayPage = pageNumber != null ? pageNumber + 1 : null;

  const label = batesNumber
    ? batesNumber
    : displayPage != null
      ? intl.formatMessage(
          { id: "chronology.source.page" },
          { page: displayPage }
        )
      : null;

  if (!label) return null;

  const title =
    batesNumber && displayPage != null
      ? intl.formatMessage(
          { id: "chronology.source.pageWithBates" },
          { page: displayPage, bates: batesNumber }
        )
      : batesNumber
        ? batesNumber
        : intl.formatMessage(
            { id: "chronology.source.page" },
            { page: displayPage }
          );

  const canNavigate = Boolean(pageId);

  return (
    <SourceBadge
      disabled={!canNavigate}
      title={title}
      className="max-w-[10rem]"
      onClick={
        canNavigate
          ? () => {
              trackEvent({
                eventName: "app/chronology/source.badge/click",
              });
              updateParams({ pageId });
            }
          : undefined
      }
    >
      <span className="min-w-0 truncate">{label}</span>
    </SourceBadge>
  );
};
